import { useSelector } from 'react-redux';
import { CartPrescriptionEye } from '../../types';
import {
  HydraBoostDailyTrialData,
  VitaminBoostDailyTrialData,
} from '../../data';
import {
  useCreateCartProductVariantEffect,
  useModifyCartProductVariantEffect,
  useRemoveCartProductVariantEffect,
  useCreateCartTrialEffect,
  useModifyCartTrialEffect,
  useRemoveCartTrialEffect,
  useCreateCartPrescriptionEffect,
  useModifyCartPrescriptionEffect,
  useCreateCartDoctorEffect,
  useModifyCartDoctorEffect,
  CartDoctorDetails,
  StoreState,
  useBasketEffect,
} from '../../services';
import { CatalogProduct } from '@hiwaldo/sdk/interfaces';

export function useShoppingCartEffect(callback?: () => void) {
  const cartState = useSelector((state: StoreState) => state.orders.cart);

  const { showBasket, hideBasket, basketOpen } = useBasketEffect();
  const { createCartProductVariant } =
    useCreateCartProductVariantEffect(callback);
  const { modifyCartProductVariant } =
    useModifyCartProductVariantEffect(callback);
  const { removeCartProductVariant } =
    useRemoveCartProductVariantEffect(callback);
  const { createCartTrial } = useCreateCartTrialEffect(callback);
  const { modifyCartTrial } = useModifyCartTrialEffect(callback);
  const { removeCartTrial } = useRemoveCartTrialEffect(callback);
  const { createCartPrescription } = useCreateCartPrescriptionEffect(callback);
  const { modifyCartPrescription } = useModifyCartPrescriptionEffect(callback);
  const { createCartDoctor } = useCreateCartDoctorEffect(callback);
  const { modifyCartDoctor } = useModifyCartDoctorEffect(callback);

  const addCartProductVariant = (
    productVariantId: number,
    quantity: number
  ) => {
    createCartProductVariant(productVariantId, quantity);
  };

  const updateCartProductVariant = (
    variantId: number,
    productVariantId: number,
    quantity: number
  ) => {
    modifyCartProductVariant(variantId, productVariantId, quantity);
  };

  const clearCartProductVariant = (productVariantId: number) => {
    const cartProductVariant = cartState.cart!.productVariants.filter(
      (variant) => variant.productVariantId === productVariantId
    );

    if (cartProductVariant.length) {
      removeCartProductVariant(cartProductVariant[0].id);
    }
  };

  const addTrialToCart = (trialId: number) => {
    if (cartState.includesTrial) {
      modifyCartTrial(
        +cartState.cart!.trials[0].id,
        trialId,
        1,
        undefined,
        undefined
      );
    } else {
      createCartTrial(trialId, 1);
    }
  };

  const clearCartTrial = () => {
    if (cartState.includesTrial) {
      removeCartTrial(+cartState.cart!.trials[0].id);
    }
  };

  const addPrescriptionToCart = (
    leftEye?: CartPrescriptionEye,
    rightEye?: CartPrescriptionEye
  ) => {
    if (cartState.includesTrial) {
      if (cartState.includesPrescription) {
        modifyCartPrescription(
          +cartState.cart!.prescription!.id,
          leftEye,
          rightEye
        );
      } else {
        createCartPrescription(leftEye, rightEye);
      }

      const leftEyeTrialVariant = leftEye
        ? findTrialVariant(+cartState.cart!.trials[0].trial.id, leftEye.power)
        : undefined;
      const rightEyeTrialVariant = rightEye
        ? findTrialVariant(+cartState.cart!.trials[0].trial.id, rightEye.power)
        : undefined;

      modifyCartTrial(
        +cartState.cart!.trials[0].id,
        +cartState.cart!.trials[0].trial.id,
        1,
        leftEyeTrialVariant,
        rightEyeTrialVariant
      );
    }
  };

  const addDoctorToCart = (doctor: CartDoctorDetails) => {
    if (cartState.includesDoctor) {
      modifyCartDoctor(+cartState.cart!.doctor!.id, doctor);
    } else {
      createCartDoctor(doctor);
    }
  };

  return {
    cartState,
    basketOpen,
    showBasket,
    hideBasket,
    addCartProductVariant,
    updateCartProductVariant,
    clearCartProductVariant,
    addTrialToCart,
    clearCartTrial,
    addPrescriptionToCart,
    addDoctorToCart,
  };
}

const findTrialVariant = (trialId: number, power: string): number => {
  const product = [1, 2].includes(trialId || 0)
    ? (HydraBoostDailyTrialData as any as CatalogProduct)
    : (VitaminBoostDailyTrialData as any as CatalogProduct);

  for (const i in product.productVariants) {
    for (const x in product.productVariants[i].attributes) {
      const { attribute } = product.productVariants[i].attributes[x];
      if (attribute.title.includes('Power') && attribute.value === power) {
        return product.productVariants[i].id;
      }
    }
  }

  return 0;
};
